import React, { useState } from 'react';

const ITEMS = [
  { id: 'i1',  label: '🖥️ Monitor',        correctBin: 'Electronics' },
  { id: 'i2',  label: '🪑 Office Chair',    correctBin: 'Furniture' },
  { id: 'i3',  label: '⌨️ Keyboard',        correctBin: 'Electronics' },
  { id: 'i4',  label: '📚 Bookshelf',       correctBin: 'Furniture' },
  { id: 'i5',  label: '🖱️ Mouse',           correctBin: 'Electronics' },
  { id: 'i6',  label: '📝 Sticky Notes',    correctBin: 'Stationery' },
  { id: 'i7',  label: '🖊️ Pen Set',         correctBin: 'Stationery' },
  { id: 'i8',  label: '📐 Ruler',           correctBin: 'Stationery' },
  { id: 'i9',  label: '🗄️ Filing Cabinet',  correctBin: 'Furniture' },
  { id: 'i10', label: '🔌 USB Hub',         correctBin: 'Electronics' },
];

const BINS = ['Electronics', 'Furniture', 'Stationery'];
const BIN_COLORS = { Electronics: '#1e88e5', Furniture: '#6d4c41', Stationery: '#43a047' };
const BIN_ICONS  = { Electronics: '⚡', Furniture: '🪵', Stationery: '✏️' };

export default function C23_DragBinSort({ onComplete }) {
  const [unplaced, setUnplaced] = useState(ITEMS.map(i => i.id));
  const [bins, setBins] = useState({ Electronics: [], Furniture: [], Stationery: [] });
  const [dragging, setDragging] = useState(null);
  const [draggingFrom, setDraggingFrom] = useState(null);
  const [dragOverBin, setDragOverBin] = useState(null);
  const [verified, setVerified] = useState(false);

  const getItem = (id) => ITEMS.find(i => i.id === id);

  const onDragStart = (id, from) => {
    setDragging(id);
    setDraggingFrom(from);
  };

  const onDragOver = (e, bin) => {
    e.preventDefault();
    setDragOverBin(bin);
  };

  const onDragLeave = () => setDragOverBin(null);

  const onDropToBin = (e, bin) => {
    e.preventDefault();
    setDragOverBin(null);
    if (!dragging) return;

    if (draggingFrom === 'unplaced') {
      setUnplaced(prev => prev.filter(id => id !== dragging));
    } else if (draggingFrom && draggingFrom !== bin) {
      setBins(prev => ({ ...prev, [draggingFrom]: prev[draggingFrom].filter(id => id !== dragging) }));
    }

    if (draggingFrom !== bin) {
      setBins(prev => ({ ...prev, [bin]: [...prev[bin], dragging] }));
    }
    setDragging(null);
    setDraggingFrom(null);
  };

  const onDropToUnplaced = (e) => {
    e.preventDefault();
    setDragOverBin(null);
    if (!dragging || draggingFrom === 'unplaced') { setDragging(null); return; }
    if (draggingFrom) {
      setBins(prev => ({ ...prev, [draggingFrom]: prev[draggingFrom].filter(id => id !== dragging) }));
    }
    setUnplaced(prev => [...prev, dragging]);
    setDragging(null);
    setDraggingFrom(null);
  };

  const handleVerify = () => {
    setVerified(true);
    let correct = 0;
    const details = {};
    ITEMS.forEach(item => {
      const placedIn = BINS.find(bin => bins[bin].includes(item.id)) || null;
      const pass = placedIn === item.correctBin;
      if (pass) correct++;
      details[item.id] = {
        label: item.label,
        expected: item.correctBin,
        got: placedIn || 'Not placed',
        pass,
      };
    });
    const score = Math.round((correct / ITEMS.length) * 100);
    onComplete({ score, details, passed: score === 100 });
  };

  const totalPlaced = BINS.reduce((s, b) => s + bins[b].length, 0);

  return (
    <div className="ab-challenge-ui">
      <p className="ab-hint">
        Drag each item from the pool below into the correct category bin: Electronics, Furniture, or Stationery.
        Drag back to the pool to undo a placement. All 10 items must be placed correctly.
      </p>

      {/* Unplaced pool */}
      <div
        style={{
          minHeight: 64,
          background: dragOverBin === 'unplaced' ? 'var(--card2)' : 'var(--surface)',
          border: '2px dashed var(--border2)',
          borderRadius: 8,
          padding: '10px 12px',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 8,
          marginBottom: 16,
          transition: 'background 0.15s',
        }}
        onDragOver={e => { e.preventDefault(); setDragOverBin('unplaced'); }}
        onDragLeave={onDragLeave}
        onDrop={onDropToUnplaced}
      >
        {unplaced.length === 0
          ? <span style={{ fontSize: 12, opacity: 0.4, alignSelf: 'center', color: 'var(--text2)' }}>All items placed — drag back here to undo</span>
          : unplaced.map(id => {
              const item = getItem(id);
              return (
                <div
                  key={id}
                  draggable={!verified}
                  onDragStart={() => onDragStart(id, 'unplaced')}
                  style={{
                    padding: '6px 14px',
                    background: dragging === id ? 'var(--card2)' : 'var(--card)',
                    border: '1.5px solid var(--border2)',
                    color: 'var(--text)',
                    borderRadius: 20,
                    fontSize: 13,
                    cursor: verified ? 'default' : 'grab',
                    opacity: dragging === id ? 0.4 : 1,
                    whiteSpace: 'nowrap',
                    userSelect: 'none',
                  }}
                >
                  {item.label}
                </div>
              );
            })
        }
      </div>

      {/* Bins */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {BINS.map(bin => (
          <div
            key={bin}
            style={{
              flex: '1 1 160px',
              minHeight: 180,
              background: dragOverBin === bin ? `${BIN_COLORS[bin]}18` : 'var(--surface)',
              border: `2px solid ${dragOverBin === bin ? BIN_COLORS[bin] : 'var(--border2)'}`,
              color: 'var(--text)',
              borderRadius: 10,
              padding: '10px 10px',
              transition: 'all 0.15s',
            }}
            onDragOver={e => onDragOver(e, bin)}
            onDragLeave={onDragLeave}
            onDrop={e => onDropToBin(e, bin)}
          >
            <div style={{ fontWeight: 700, fontSize: 13, color: BIN_COLORS[bin], marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
              <span>{BIN_ICONS[bin]}</span> {bin}
              <span style={{ marginLeft: 'auto', fontSize: 11, opacity: 0.6 }}>{bins[bin].length}</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {bins[bin].map(id => {
                const item = getItem(id);
                const isCorrect = verified ? item.correctBin === bin : null;
                return (
                  <div
                    key={id}
                    draggable={!verified}
                    onDragStart={() => onDragStart(id, bin)}
                    style={{
                      padding: '5px 10px',
                      background: verified
                        ? isCorrect ? 'rgba(34,197,94,.12)' : 'rgba(239,68,68,.12)'
                        : 'var(--card)',
                      border: `1.5px solid ${verified ? (isCorrect ? 'var(--green)' : 'var(--red)') : 'var(--border2)'}`,
                      color: 'var(--text)',
                      borderRadius: 16,
                      fontSize: 12,
                      cursor: verified ? 'default' : 'grab',
                      opacity: dragging === id ? 0.4 : 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      userSelect: 'none',
                    }}
                  >
                    {item.label}
                    {verified && <span style={{ marginLeft: 'auto', fontSize: 13 }}>{isCorrect ? '✓' : '✗'}</span>}
                  </div>
                );
              })}
              {bins[bin].length === 0 && (
                <div style={{ fontSize: 12, opacity: 0.3, textAlign: 'center', paddingTop: 24, color: 'var(--text2)' }}>Drop here</div>
              )}
            </div>
          </div>
        ))}
      </div>

      <div style={{ marginTop: 10, fontSize: 12, opacity: 0.6, color: 'var(--text2)' }}>
        {totalPlaced}/{ITEMS.length} items placed · {unplaced.length} remaining
      </div>

      {!verified
        ? (
          <button
            className="ab-action-btn"
            style={{ marginTop: 14 }}
            onClick={handleVerify}
            disabled={totalPlaced < ITEMS.length}
            title={totalPlaced < ITEMS.length ? 'Place all items before verifying' : ''}
          >
            Verify Sorting
          </button>
        )
        : <div className="ab-submitted-badge">✓ Sort Verified — Evaluating...</div>
      }
    </div>
  );
}
